import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  TextField,
  MenuItem,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  Menu,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Agriculture as AgricultureIcon,
  Inventory as InventoryIcon,
  Store as StoreIcon,
  ShoppingCart as ShoppingCartIcon,
  Gavel as GavelIcon,
  Campaign as CampaignIcon,
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Assignment as AssignmentIcon,
  PictureAsPdf as PdfIcon,
  TableChart as CsvIcon
} from '@mui/icons-material';
import { apiService } from '@/services/api';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Papa from 'papaparse';

interface DashboardData {
  users: any;
  productions: any;
  harvests: any;
  orders: any;
  auctions: any;
  communications: any;
}

const Reports: React.FC = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [reportData, setReportData] = useState<any>(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [filters, setFilters] = useState<any>({});
  const [exportAnchorEl, setExportAnchorEl] = useState<null | HTMLElement>(null);

  const reportTabs = [
    { label: 'Dashboard Overview', icon: <DashboardIcon />, value: 'dashboard' },
    { label: 'Users', icon: <PeopleIcon />, value: 'users' },
    { label: 'Buyer Requirements', icon: <AssignmentIcon />, value: 'buyer-requirements' },
    { label: 'Productions', icon: <AgricultureIcon />, value: 'productions' },
    { label: 'Harvests', icon: <InventoryIcon />, value: 'harvests' },
    { label: 'Marketplace', icon: <StoreIcon />, value: 'marketplace' },
    { label: 'Orders', icon: <ShoppingCartIcon />, value: 'orders' },
    { label: 'Auctions', icon: <GavelIcon />, value: 'auctions' },
    { label: 'Communications', icon: <CampaignIcon />, value: 'communications' },
    { label: 'Financial', icon: <MoneyIcon />, value: 'financial' },
    { label: 'System Activity', icon: <TrendingUpIcon />, value: 'system-activity' }
  ];

  useEffect(() => {
    loadReport();
  }, [currentTab, dateFrom, dateTo, filters]);

  const loadReport = async () => {
    setLoading(true);
    try {
      const reportType = reportTabs[currentTab].value;
      const params = new URLSearchParams();
      
      if (dateFrom) params.append('dateFrom', dateFrom);
      if (dateTo) params.append('dateTo', dateTo);
      
      Object.keys(filters).forEach(key => {
        if (filters[key]) params.append(key, filters[key]);
      });

      const response = await apiService.getReport(reportType, params.toString());
      
      if (reportType === 'dashboard') {
        setDashboardData(response.data as DashboardData);
      } else {
        setReportData(response.data);
      }
    } catch (error) {
      console.error('Error loading report:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportClick = (event: React.MouseEvent<HTMLElement>) => {
    setExportAnchorEl(event.currentTarget);
  };

  const handleExportClose = () => {
    setExportAnchorEl(null);
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    const reportType = reportTabs[currentTab].label;
    
    doc.setFontSize(16);
    doc.text(reportType, 14, 15);
    doc.setFontSize(10);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 22);
    
    if (dateFrom && dateTo) {
      doc.text(`Period: ${dateFrom} to ${dateTo}`, 14, 28);
    }

    if (reportData) {
      const data = reportData.users || reportData.requirements || reportData.productions || 
                   reportData.harvests || reportData.orders || reportData.auctions || 
                   reportData.communications || [];
      
      if (data.length > 0) {
        const columns = Object.keys(data[0]).filter(key => key !== 'id');
        const rows = data.map((row: any) => 
          columns.map(col => row[col]?.toString() || '-')
        );

        autoTable(doc, {
          head: [columns.map(col => col.replace(/_/g, ' ').toUpperCase())],
          body: rows,
          startY: dateFrom && dateTo ? 32 : 26,
          styles: { fontSize: 8 },
          headStyles: { fillColor: [41, 128, 185] }
        });
      }
    }

    doc.save(`${reportType.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`);
    handleExportClose();
  };

  const exportToCSV = () => {
    const reportType = reportTabs[currentTab].label;
    
    if (reportData) {
      const data = reportData.users || reportData.requirements || reportData.productions || 
                   reportData.harvests || reportData.orders || reportData.auctions || 
                   reportData.communications || [];
      
      if (data.length > 0) {
        const cleanData = data.map((row: any) => {
          const { id, ...rest } = row;
          return rest;
        });

        const csv = Papa.unparse(cleanData);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        
        link.setAttribute('href', url);
        link.setAttribute('download', `${reportType.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }
    handleExportClose();
  };

  const renderDashboardOverview = () => {
    if (!dashboardData) return null;

    return (
      <Grid container spacing={3}>
        <Grid item xs={12} md={6} lg={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PeopleIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                <Typography variant="h6">Users</Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Total Farmers</Typography>
                  <Typography variant="h5">{dashboardData.users.total_farmers || 0}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Total Buyers</Typography>
                  <Typography variant="h5">{dashboardData.users.total_buyers || 0}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Active Farmers</Typography>
                  <Typography variant="h5" color="success.main">{dashboardData.users.active_farmers || 0}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Pending Approvals</Typography>
                  <Typography variant="h5" color="warning.main">{dashboardData.users.pending_approvals || 0}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AgricultureIcon sx={{ fontSize: 40, color: 'success.main', mr: 2 }} />
                <Typography variant="h6">Productions</Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Total</Typography>
                  <Typography variant="h5">{dashboardData.productions.total_productions || 0}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Active</Typography>
                  <Typography variant="h5" color="success.main">{dashboardData.productions.active_productions || 0}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Completed</Typography>
                  <Typography variant="h5">{dashboardData.productions.completed_productions || 0}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ShoppingCartIcon sx={{ fontSize: 40, color: 'info.main', mr: 2 }} />
                <Typography variant="h6">Orders & Revenue</Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Total Revenue</Typography>
                  <Typography variant="h4" color="success.main">
                    ${parseFloat(dashboardData.orders.total_revenue || 0).toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Total Orders</Typography>
                  <Typography variant="h5">{dashboardData.orders.total_orders || 0}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Completed</Typography>
                  <Typography variant="h5" color="success.main">{dashboardData.orders.completed_orders || 0}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <InventoryIcon sx={{ fontSize: 40, color: 'warning.main', mr: 2 }} />
                <Typography variant="h6">Harvests</Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Total Harvests</Typography>
                  <Typography variant="h5">{dashboardData.harvests.total_harvests || 0}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Available</Typography>
                  <Typography variant="h5" color="success.main">{dashboardData.harvests.available_harvests || 0}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Total Quantity</Typography>
                  <Typography variant="h5">{parseFloat(dashboardData.harvests.total_quantity || 0).toLocaleString()}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <GavelIcon sx={{ fontSize: 40, color: 'secondary.main', mr: 2 }} />
                <Typography variant="h6">Auctions</Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Total Auctions</Typography>
                  <Typography variant="h5">{dashboardData.auctions.total_auctions || 0}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Active</Typography>
                  <Typography variant="h5" color="success.main">{dashboardData.auctions.active_auctions || 0}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Total Value</Typography>
                  <Typography variant="h5">
                    ${parseFloat(dashboardData.auctions.total_auction_value || 0).toLocaleString()}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CampaignIcon sx={{ fontSize: 40, color: 'error.main', mr: 2 }} />
                <Typography variant="h6">Communications</Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Total Sent</Typography>
                  <Typography variant="h5">{dashboardData.communications.total_communications || 0}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Recipients</Typography>
                  <Typography variant="h5">{dashboardData.communications.total_recipients || 0}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Delivered</Typography>
                  <Typography variant="h5" color="success.main">{dashboardData.communications.successful_deliveries || 0}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const getReportSpecificCards = () => {
    const reportType = reportTabs[currentTab].value;
    
    if (!reportData || !reportData.summary) return null;

    switch (reportType) {
      case 'users':
        return (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {reportData.summary.map((item: any, idx: number) => (
              <Grid item xs={12} sm={6} md={3} key={idx}>
                <Card>
                  <CardContent>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {item.role?.toUpperCase() || 'Total'}
                    </Typography>
                    <Typography variant="h4">{item.count || 0}</Typography>
                    <Typography variant="caption" color="success.main">
                      {item.active_count || 0} Active
                    </Typography>
                    {' • '}
                    <Typography variant="caption" color="warning.main">
                      {item.pending_count || 0} Pending
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        );

      case 'buyer-requirements':
        const totalReqs = reportData.requirements?.length || 0;
        const activeReqs = reportData.requirements?.filter((r: any) => r.status === 'active').length || 0;
        const fulfilledReqs = reportData.requirements?.filter((r: any) => r.status === 'fulfilled').length || 0;
        const totalBudget = reportData.totalBudget || 0;
        
        return (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">Total Requirements</Typography>
                  <Typography variant="h4">{totalReqs}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">Active</Typography>
                  <Typography variant="h4" color="success.main">{activeReqs}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">Fulfilled</Typography>
                  <Typography variant="h4" color="info.main">{fulfilledReqs}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">Total Budget</Typography>
                  <Typography variant="h4" color="primary.main">
                    ${parseFloat(totalBudget).toLocaleString()}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        );

      case 'productions':
        return (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {reportData.summary.map((item: any, idx: number) => (
              <Grid item xs={12} sm={6} md={3} key={idx}>
                <Card>
                  <CardContent>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {item.status?.toUpperCase() || 'Status'}
                    </Typography>
                    <Typography variant="h4">{item.count || 0}</Typography>
                    <Typography variant="caption">
                      Target: {parseFloat(item.total_target || 0).toLocaleString()}
                    </Typography>
                    <br />
                    <Typography variant="caption" color="success.main">
                      Actual: {parseFloat(item.total_actual || 0).toLocaleString()}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        );

      case 'orders':
        return (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {reportData.summary.map((item: any, idx: number) => (
              <Grid item xs={12} sm={6} md={3} key={idx}>
                <Card>
                  <CardContent>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {item.status?.toUpperCase() || 'Status'}
                    </Typography>
                    <Typography variant="h4">{item.order_count || 0}</Typography>
                    <Typography variant="h6" color="success.main">
                      ${parseFloat(item.total_revenue || 0).toLocaleString()}
                    </Typography>
                    <Typography variant="caption">
                      Avg: ${parseFloat(item.avg_order_value || 0).toFixed(2)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        );

      case 'auctions':
        return (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {reportData.summary.map((item: any, idx: number) => (
              <Grid item xs={12} sm={6} md={3} key={idx}>
                <Card>
                  <CardContent>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {item.status?.toUpperCase() || 'Status'}
                    </Typography>
                    <Typography variant="h4">{item.lot_count || 0}</Typography>
                    <Typography variant="h6" color="success.main">
                      ${parseFloat(item.total_value || 0).toLocaleString()}
                    </Typography>
                    <Typography variant="caption">
                      Avg Price: ${parseFloat(item.avg_final_price || 0).toFixed(2)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        );

      default:
        return (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {reportData.summary.slice(0, 4).map((item: any, idx: number) => (
              <Grid item xs={12} sm={6} md={3} key={idx}>
                <Card>
                  <CardContent>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {Object.values(item)[0]?.toString().toUpperCase() || 'Summary'}
                    </Typography>
                    <Typography variant="h4">{item.count || item.total || 0}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        );
    }
  };

  const renderFilters = () => {
    const reportType = reportTabs[currentTab].value;
    
    return (
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="From Date"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="To Date"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>

          {reportType === 'users' && (
            <>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={filters.role || ''}
                    label="Role"
                    onChange={(e) => setFilters({ ...filters, role: e.target.value })}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="farmer">Farmer</MenuItem>
                    <MenuItem value="buyer">Buyer</MenuItem>
                    <MenuItem value="advisor">Advisor</MenuItem>
                    <MenuItem value="admin">Admin</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filters.status || ''}
                    label="Status"
                    onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="suspended">Suspended</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </>
          )}

          {reportType === 'buyer-requirements' && (
            <>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filters.status || ''}
                    label="Status"
                    onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="fulfilled">Fulfilled</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={filters.category || ''}
                    label="Category"
                    onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="crops">Crops</MenuItem>
                    <MenuItem value="livestock">Livestock</MenuItem>
                    <MenuItem value="equipment">Equipment</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </>
          )}

          {(reportType === 'productions' || reportType === 'harvests') && (
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status || ''}
                  label="Status"
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="available">Available</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}

          {reportType === 'orders' && (
            <>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filters.status || ''}
                    label="Status"
                    onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="processing">Processing</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Payment Status</InputLabel>
                  <Select
                    value={filters.payment_status || ''}
                    label="Payment Status"
                    onChange={(e) => setFilters({ ...filters, payment_status: e.target.value })}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="paid">Paid</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="failed">Failed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </>
          )}

          {reportType === 'auctions' && (
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status || ''}
                  label="Status"
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="ended">Ended</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}

          {reportType === 'communications' && (
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Type</InputLabel>
                <Select
                  value={filters.type || ''}
                  label="Type"
                  onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="sms">SMS</MenuItem>
                  <MenuItem value="email">Email</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}

          <Grid item xs={12} sm={6} md={2}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                setDateFrom('');
                setDateTo('');
                setFilters({});
              }}
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>
    );
  };

  const renderDataTable = () => {
    if (!reportData) return null;

    const data = reportData.users || reportData.requirements || reportData.productions || 
                 reportData.harvests || reportData.orders || reportData.auctions || 
                 reportData.communications || [];

    if (data.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary">No data available for the selected filters</Typography>
        </Box>
      );
    }

    const reportType = reportTabs[currentTab].value;
    const columns = Object.keys(data[0] || {}).filter(key => key !== 'id').slice(0, 10);
    const isBuyerRequirements = reportType === 'buyer-requirements';

    return (
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell key={col}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {col.replace(/_/g, ' ').toUpperCase()}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.slice(0, 100).map((row: any, idx: number) => (
              <TableRow key={idx} hover>
                {columns.map((col) => (
                  <TableCell key={col}>
                    {typeof row[col] === 'boolean' ? (
                      <Chip label={row[col] ? 'Yes' : 'No'} size="small" color={row[col] ? 'success' : 'default'} />
                    ) : col.includes('status') ? (
                      <Chip 
                        label={row[col]?.toString() || '-'} 
                        size="small" 
                        color={
                          row[col] === 'active' || row[col] === 'completed' || row[col] === 'paid' ? 'success' :
                          row[col] === 'pending' ? 'warning' :
                          row[col] === 'cancelled' || row[col] === 'failed' ? 'error' : 'default'
                        }
                      />
                    ) : col === 'budget' || col.includes('price') || col.includes('amount') || col.includes('revenue') ? (
                      '$' + parseFloat(row[col] || 0).toLocaleString()
                    ) : col.includes('date') || col === 'created' || col === 'delivery_period' ? (
                      new Date(row[col]).toLocaleDateString()
                    ) : (
                      row[col]?.toString() || '-'
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {isBuyerRequirements && reportData.totalBudget && (
              <TableRow sx={{ bgcolor: 'action.hover', fontWeight: 'bold' }}>
                <TableCell colSpan={columns.indexOf('budget')}>
                  <Typography variant="subtitle1" fontWeight="bold">TOTAL</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary.main">
                    ${parseFloat(reportData.totalBudget).toLocaleString()}
                  </Typography>
                </TableCell>
                <TableCell colSpan={columns.length - columns.indexOf('budget') - 1} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Reports & Analytics
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Comprehensive reports for data-driven decision making
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <IconButton onClick={loadReport} disabled={loading}>
            <RefreshIcon />
          </IconButton>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleExportClick}
            disabled={loading || currentTab === 0}
          >
            Export
          </Button>
          <Menu
            anchorEl={exportAnchorEl}
            open={Boolean(exportAnchorEl)}
            onClose={handleExportClose}
          >
            <MenuItem onClick={exportToPDF}>
              <ListItemIcon>
                <PdfIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Export as PDF</ListItemText>
            </MenuItem>
            <MenuItem onClick={exportToCSV}>
              <ListItemIcon>
                <CsvIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Export as CSV</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {currentTab !== 0 && renderFilters()}

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={currentTab}
          onChange={(_, newValue) => setCurrentTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          {reportTabs.map((tab, index) => (
            <Tab
              key={index}
              icon={tab.icon}
              label={tab.label}
              iconPosition="start"
            />
          ))}
        </Tabs>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box>
          {currentTab === 0 ? renderDashboardOverview() : (
            <>
              {getReportSpecificCards()}
              {renderDataTable()}
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

export default Reports;
